/**
 * 폴리곤 타입 구분 및 크기 계산 유틸리티
 * 다양한 사각형 타입에 따른 정확한 크기 계산을 제공합니다.
 */
export class PolygonTypeCalculator {

  /**
   * 사각형 종류 감지 (개선된 버전)
   * @param {Array} coordinates - 폴리곤 좌표 배열
   * @returns {string} 사각형 종류
   */
  detectQuadrilateralType(coordinates) {

    // 각도 계산
    const angles = this.calculateAngles(coordinates);

    // 변의 길이 계산
    const sideLengths = this.calculateSideLengths(coordinates);

    // 직각 개수 확인 (90도 ± 5도 허용)
    const rightAngles = angles.filter(angle =>
      Math.abs(angle - Math.PI/2) < 0.087 // 약 5도
    ).length;

    // 평행한 변 확인
    const parallelSides = this.countParallelSides(coordinates);

    // 변의 길이 일치 여부 확인
    const uniqueSideLengths = [...new Set(sideLengths.map(length =>
      Math.round(length * 1000) / 1000 // 소수점 3자리까지 반올림
    ))];

    // 모든 변의 길이가 같은지 확인
    const allSidesEqual = uniqueSideLengths.length === 1;

    // 두 쌍의 인접한 변의 길이가 같은지 확인 (연 모양)
    const isKite = this.isKiteShape(sideLengths);

    // 정사각형 판별
    if (rightAngles === 4 && allSidesEqual) {
      return '정사각형';
    }

    // 직사각형 판별
    if (rightAngles === 4 && !allSidesEqual) {
      return '직사각형';
    }

    // 마름모 판별
    if (rightAngles === 0 && allSidesEqual && parallelSides === 2) {
      return '마름모';
    }

    // 평행사변형 판별
    if (rightAngles === 0 && parallelSides === 2 && !allSidesEqual) {
      return '평행사변형';
    }

    // 연 모양 판별
    if (rightAngles === 0 && isKite && !allSidesEqual) {
      return '연';
    }

    // 사다리꼴 판별
    if (parallelSides === 1) {
      return '사다리꼴';
    }

    // 회전된 직사각형 판별 (직각이 2개이고 대변 평행)
    if (rightAngles === 2 && parallelSides === 2) {
      return '회전된직사각형';
    }

    // 일반 사각형
    return '일반사각형';
  }

  /**
   * 사각형 종류별 정확한 크기 계산
   * @param {Array} coordinates - 폴리곤 좌표 배열
   * @param {string} quadrilateralType - 사각형 종류 (선택사항, 자동 감지됨)
   * @returns {Object} 크기 정보 {width, height, type}
   */
  calculateAccuratePolygonSize(coordinates, quadrilateralType = null) {
    const type = quadrilateralType || this.detectQuadrilateralType(coordinates);

    let size;
    switch (type) {
      case '정사각형':
        size = this.calculate정사각형Size(coordinates);
        break;
      case '직사각형':
        size = this.calculate직사각형Size(coordinates);
        break;
      case '마름모':
        size = this.calculate마름모Size(coordinates);
        break;
      case '평행사변형':
        size = this.calculate평행사변형Size(coordinates);
        break;
      case '연':
        size = this.calculate연Size(coordinates);
        break;
      case '사다리꼴':
        size = this.calculate사다리꼴Size(coordinates);
        break;
      case '회전된직사각형':
        size = this.calculate회전된직사각형Size(coordinates);
        break;
      default:
        size = this.calculate회전된직사각형Size(coordinates);
        break;
    }
    return {
      width: size.width,
      height: size.height,
      type: type
    };
  }

  /**
   * 정사각형 크기 계산
   * @param {Array} coordinates - 폴리곤 좌표 배열
   * @returns {Object} 크기 정보 {width, height}
   */
  calculate정사각형Size(coordinates) {
    // 정사각형의 경우 모든 변의 길이가 같음
    const sideLengths = this.calculateSideLengths(coordinates);
    const sideLength = sideLengths[0]; // 모든 변의 길이가 같음

    return { width: sideLength, height: sideLength };
  }

  /**
   * 직사각형 크기 계산 (가장 정확)
   * @param {Array} coordinates - 폴리곤 좌표 배열
   * @returns {Object} 크기 정보 {width, height}
   */
  calculate직사각형Size(coordinates) {
    // 직사각형의 경우 대각선 좌표 차이가 정확
    const width = Math.abs(coordinates[1][0] - coordinates[0][0]);
    const height = Math.abs(coordinates[0][1] - coordinates[3][1]);

    return { width, height };
  }

  /**
   * 마름모 크기 계산 (대각선 기반)
   * @param {Array} coordinates - 폴리곤 좌표 배열
   * @returns {Object} 크기 정보 {width, height}
   */
  calculate마름모Size(coordinates) {
    // 마름모의 대각선 계산
    const diagonal1 = Math.sqrt(
      Math.pow(coordinates[2][0] - coordinates[0][0], 2) +
      Math.pow(coordinates[2][1] - coordinates[0][1], 2)
    );
    const diagonal2 = Math.sqrt(
      Math.pow(coordinates[3][0] - coordinates[1][0], 2) +
      Math.pow(coordinates[3][1] - coordinates[1][1], 2)
    );

    // 마름모의 면적 = (대각선1 × 대각선2) / 2
    const area = (diagonal1 * diagonal2) / 2;

    // 면적을 고려한 유효 크기 계산
    const effectiveSize = Math.sqrt(area);

    return { width: effectiveSize, height: effectiveSize };
  }

  /**
   * 평행사변형 크기 계산 (벡터 기반)
   * @param {Array} coordinates - 폴리곤 좌표 배열
   * @returns {Object} 크기 정보 {width, height}
   */
  calculate평행사변형Size(coordinates) {
    // 평행사변형의 두 변 벡터 계산
    const vector1 = [
      coordinates[1][0] - coordinates[0][0],
      coordinates[1][1] - coordinates[0][1]
    ];
    const vector2 = [
      coordinates[3][0] - coordinates[0][0],
      coordinates[3][1] - coordinates[0][1]
    ];

    // 벡터의 크기 계산
    const width = Math.sqrt(vector1[0] * vector1[0] + vector1[1] * vector1[1]);
    const height = Math.sqrt(vector2[0] * vector2[0] + vector2[1] * vector2[1]);

    return { width, height };
  }

  /**
   * 연 모양 크기 계산 (대각선 기반)
   * @param {Array} coordinates - 폴리곤 좌표 배열
   * @returns {Object} 크기 정보 {width, height}
   */
  calculate연Size(coordinates) {
    // 연의 대각선 계산
    const diagonal1 = Math.sqrt(
      Math.pow(coordinates[2][0] - coordinates[0][0], 2) +
      Math.pow(coordinates[2][1] - coordinates[0][1], 2)
    );
    const diagonal2 = Math.sqrt(
      Math.pow(coordinates[3][0] - coordinates[1][0], 2) +
      Math.pow(coordinates[3][1] - coordinates[1][1], 2)
    );

    // 연의 면적 = (대각선1 × 대각선2) / 2
    const area = (diagonal1 * diagonal2) / 2;

    // 면적을 고려한 유효 크기 계산
    const effectiveSize = Math.sqrt(area);

    return { width: effectiveSize, height: effectiveSize };
  }

  /**
   * 사다리꼴 크기 계산 (평균 기반)
   * @param {Array} coordinates - 폴리곤 좌표 배열
   * @returns {Object} 크기 정보 {width, height}
   */
  calculate사다리꼴Size(coordinates) {
    // 사다리꼴의 경우 평균값 사용
    const widths = [
      Math.abs(coordinates[1][0] - coordinates[0][0]),
      Math.abs(coordinates[2][0] - coordinates[3][0])
    ];
    const heights = [
      Math.abs(coordinates[0][1] - coordinates[3][1]),
      Math.abs(coordinates[1][1] - coordinates[2][1])
    ];

    const width = (widths[0] + widths[1]) / 2;
    const height = (heights[0] + heights[1]) / 2;

    return { width, height };
  }

  /**
   * 회전된 직사각형 크기 계산 (벡터 길이 기반)
   * @param {Array} coordinates - 폴리곤 좌표 배열
   * @returns {Object} 크기 정보 {width, height}
   */
  calculate회전된직사각형Size(coordinates) {
    // 실제 가로세로 길이 계산 (벡터 기반)
    const {width, height} = this.calculateActualPolygonSize(coordinates);

    // 긴쪽을 width, 짧은 쪽을 height로 설정

    console.log('[PolygonTypeCalculator] 회전된 직사각형 크기 계산 (벡터 길이 기반):', {
      width: width,
      height: height,
      aspectRatio: width / height
    });

    return { width, height };
  }

  /**
   * 일반 사각형 크기 계산 (면적 기반)
   * @param {Array} coordinates - 폴리곤 좌표 배열
   * @returns {Object} 크기 정보 {width, height}
   */
  calculate일반사각형Size(coordinates) {
    // 면적 계산
    const area = this.calculatePolygonArea(coordinates);

    // 경계 상자 계산
    const bounds = this.calculateBoundingBox(coordinates);
    const boundingWidth = bounds.maxX - bounds.minX;
    const boundingHeight = bounds.maxY - bounds.minY;

    // 면적을 고려한 유효 크기 계산
    const effectiveWidth = Math.sqrt(area * boundingWidth / boundingHeight);
    const effectiveHeight = Math.sqrt(area * boundingHeight / boundingWidth);

    return { width: effectiveWidth, height: effectiveHeight };
  }

  /**
   * 각도 계산
   * @param {Array} coordinates - 폴리곤 좌표 배열
   * @returns {Array} 각도 배열 (라디안)
   */
  calculateAngles(coordinates) {
    const angles = [];
    const n = coordinates.length;

    for (let i = 0; i < n; i++) {
      const p1 = coordinates[i];
      const p2 = coordinates[(i + 1) % n];
      const p3 = coordinates[(i + 2) % n];

      const angle = this.calculateAngle(p1, p2, p3);
      angles.push(angle);
    }

    return angles;
  }

  /**
   * 세 점으로 각도 계산
   * @param {Array} p1 - 첫 번째 점 [x, y]
   * @param {Array} p2 - 두 번째 점 [x, y]
   * @param {Array} p3 - 세 번째 점 [x, y]
   * @returns {number} 각도 (라디안)
   */
  calculateAngle(p1, p2, p3) {
    const vector1 = [p1[0] - p2[0], p1[1] - p2[1]];
    const vector2 = [p3[0] - p2[0], p3[1] - p2[1]];

    const dotProduct = vector1[0] * vector2[0] + vector1[1] * vector2[1];
    const magnitude1 = Math.sqrt(vector1[0] * vector1[0] + vector1[1] * vector1[1]);
    const magnitude2 = Math.sqrt(vector2[0] * vector2[0] + vector2[1] * vector2[1]);

    const cosAngle = dotProduct / (magnitude1 * magnitude2);
    return Math.acos(Math.max(-1, Math.min(1, cosAngle)));
  }

  /**
   * 평행한 변의 개수 계산
   * @param {Array} coordinates - 폴리곤 좌표 배열
   * @returns {number} 평행한 변의 개수
   */
  countParallelSides(coordinates) {
    let parallelCount = 0;
    const n = coordinates.length;

    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n;
      const k = (i + 2) % n;
      const l = (i + 3) % n;

      const side1 = [coordinates[j][0] - coordinates[i][0], coordinates[j][1] - coordinates[i][1]];
      const side2 = [coordinates[l][0] - coordinates[k][0], coordinates[l][1] - coordinates[k][1]];

      // 두 벡터가 평행한지 확인 (외적이 0에 가까움)
      const crossProduct = side1[0] * side2[1] - side1[1] * side2[0];
      if (Math.abs(crossProduct) < 0.001) {
        parallelCount++;
      }
    }

    return parallelCount;
  }

  /**
   * 폴리곤 면적 계산 (사선 공식)
   * @param {Array} coordinates - 폴리곤 좌표 배열
   * @returns {number} 면적
   */
  calculatePolygonArea(coordinates) {
    let area = 0;
    const n = coordinates.length;

    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n;
      area += coordinates[i][0] * coordinates[j][1];
      area -= coordinates[j][0] * coordinates[i][1];
    }

    return Math.abs(area) / 2;
  }

  /**
   * 경계 상자 계산
   * @param {Array} coordinates - 폴리곤 좌표 배열
   * @returns {Object} 경계 상자 {minX, maxX, minY, maxY}
   */
  calculateBoundingBox(coordinates) {
    const xs = coordinates.map(coord => coord[0]);
    const ys = coordinates.map(coord => coord[1]);

    return {
      minX: Math.min(...xs),
      maxX: Math.max(...xs),
      minY: Math.min(...ys),
      maxY: Math.max(...ys)
    };
  }

  /**
   * 폴리곤 정보 요약
   * @param {Array} coordinates - 폴리곤 좌표 배열
   * @returns {Object} 폴리곤 정보 {type, size, area, angles}
   */
  getPolygonInfo(coordinates) {
    const type = this.detectQuadrilateralType(coordinates);
    const size = this.calculateAccuratePolygonSize(coordinates, type);
    const area = this.calculatePolygonArea(coordinates);
    const angles = this.calculateAngles(coordinates);

    return {
      type: type,
      size: size,
      area: area,
      angles: angles.map(angle => angle * 180 / Math.PI) // 도 단위로 변환
    };
  }

  /**
   * 변의 길이 계산
   * @param {Array} coordinates - 폴리곤 좌표 배열
   * @returns {Array} 변의 길이 배열
   */
  calculateSideLengths(coordinates) {
    const lengths = [];
    const n = coordinates.length;

    for (let i = 0; i < n; i++) {
      const current = coordinates[i];
      const next = coordinates[(i + 1) % n];

      const length = Math.sqrt(
        Math.pow(next[0] - current[0], 2) +
        Math.pow(next[1] - current[1], 2)
      );
      lengths.push(length);
    }

    return lengths;
  }

  /**
   * 연 모양인지 확인
   * @param {Array} sideLengths - 변의 길이 배열
   * @returns {boolean} 연 모양 여부
   */
  isKiteShape(sideLengths) {
    if (sideLengths.length !== 4) return false;

    // 두 쌍의 인접한 변의 길이가 같은지 확인
    const [a, b, c, d] = sideLengths;

    // (a==b && c==d) 또는 (a==d && b==c) 형태인지 확인
    const tolerance = 0.001; // 허용 오차

    return (
      (Math.abs(a - b) < tolerance && Math.abs(c - d) < tolerance) ||
      (Math.abs(a - d) < tolerance && Math.abs(b - c) < tolerance)
    );
  }

  /**
   * 실제 폴리곤 크기 계산 (벡터 기반)
   * @param {Array} coordinates - 폴리곤 좌표 배열
   * @returns {Object} 실제 크기 {width, height}
   */
  calculateActualPolygonSize(coordinates) {

    // 두 개의 평행한 변 벡터 계산
    const vector1 = [
      coordinates[1][0] - coordinates[0][0],
      coordinates[1][1] - coordinates[0][1]
    ];
    const vector2 = [
      coordinates[3][0] - coordinates[0][0],
      coordinates[3][1] - coordinates[0][1]
    ];

    // 벡터의 크기 계산 (실제 길이)
    const length1 = Math.sqrt(vector1[0] * vector1[0] + vector1[1] * vector1[1]);
    const length2 = Math.sqrt(vector2[0] * vector2[0] + vector2[1] * vector2[1]);

    // 긴 쪽을 width로 설정
    const width = Math.max(length1, length2);
    const height = Math.min(length1, length2);

    console.log('[PolygonTypeCalculator] 실제 폴리곤 크기 계산:', {
      coordinates: coordinates.length,
      vector1: vector1,
      vector2: vector2,
      length1: length1,
      length2: length2,
      width: width, // 긴 쪽
      height: height, // 짧은 쪽
      aspectRatio: width / height
    });

    return { width, height };
  }
}

// 싱글톤 인스턴스 생성
export const polygonTypeCalculator = new PolygonTypeCalculator();
